import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import socketService from './services/socketService';

// Import landing page components
import LandingPage from './pages/LandingPage';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import Guide from './pages/FQA';

// Import pages
import Home from './pages/Home';
import Login from './pages/Login';
import Verify from './pages/Verify';
import CompleteRegistration from './pages/CompleteRegistration';
import Profile from './pages/Profile';
import Transactions from './pages/Transactions';
import PaymentCallback from './pages/PaymentCallback';
import AccountStatus from './pages/AccountStatus';
import Help from './pages/Help';

// Import user pages
import NewTestRequest from './pages/NewTestRequest';
import MyTests from './pages/MyTests';
import ReceiveImageReport from './pages/ReceiveImageReport';
import ViewOnlineResults from './pages/ViewOnlineResults';
import SubmitTicket from './pages/SubmitTicket';
import Tickets from './pages/Tickets';
import TicketDetails from './pages/TicketDetails';
import BankPayment from './pages/BankPayment';
import ViewTestResults from './pages/ViewTestResults';
import SystemMessages from './pages/SystemMessages';
import SubmitRequest from './pages/SubmitRequest';
import TestRequestGuide from './pages/TestRequestGuide';
import TestResultsGuide from './pages/TestResultsGuide';
import ViewRequestStatus from './pages/ViewRequestStatus';

// Import admin pages
import AdminLogin from './pages/admin/AdminLogin';
import Dashboard from './pages/admin/Dashboard';
import AccessControl from './pages/admin/AccessControl';
import Operators from './pages/admin/Operators';
import Users from './pages/admin/Users';
import OngoingRequests from './pages/admin/OngoingRequests';
import CompletedRequests from './pages/admin/CompletedRequests';
import OtherRequests from './pages/admin/OtherRequests';
import Calendar from './pages/admin/Calendar';
import RegionSettings from './pages/admin/RegionSettings';
import Links from './pages/admin/Links';
import AdminGuide from './pages/admin/Guide';
import AdminFAQ from './pages/admin/FAQ';
import TestPackages from './pages/admin/TestPackages';
import Insurance from './pages/admin/Insurance';
import Messages from './pages/admin/Messages';
import Commands from './pages/admin/Commands';
import ImageReportRequests from './pages/admin/ImageReportRequests';
import BankPaymentRequests from './pages/admin/BankPaymentRequests';
import AdminTickets from './pages/admin/AdminTickets';
import AdminTicketDetails from './pages/admin/AdminTicketDetails';
import Reports from './pages/admin/Reports';  // Import Reports page
import GeneralSettings from './pages/admin/GeneralSettings';
// Import user Messages page
import UserMessages from './pages/Messages';

// Import auth guards
import { ProtectedRoute, AuthRoute, AdminProtectedRoute } from './components/AuthGuard';
import DashboardLayout from './components/layout/DashboardLayout';

function App() {
  // Check for token expiration and initialize socket when the app loads
  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          // The token is a JWT - we can check its expiration
          const payload = JSON.parse(atob(token.split('.')[1]));
          const expirationTime = payload.exp * 1000; // Convert to milliseconds
          
          if (Date.now() >= expirationTime) {
            // Token has expired, clear authentication
            localStorage.removeItem('authToken');
            localStorage.removeItem('userData');
            socketService.disconnect();
          }
        } catch (error) {

          // If there's any error parsing, clear the token
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
          socketService.disconnect();
        }
      }
    };
    
    // Initialize socket connection if user is authenticated
    const initializeSocket = () => {
      const token = localStorage.getItem('authToken');

      if (token) {
        socketService.updateToken(token);
        socketService.connect();
      }
    };
    
    // Check on initial load
    checkTokenExpiration();
    initializeSocket();
    
    // Also set up periodic checks
    const interval = setInterval(checkTokenExpiration, 60000); // Check every minute
    
    // Cleanup on unmount
    return () => {
      clearInterval(interval);
      socketService.disconnect();
    };
  }, []);
  
  return (
    <Router>
      <ToastContainer position="top-center" rtl={true} />
      <Routes>
        {/* Public landing pages - accessible without authentication */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/guide" element={<Guide />} />
        <Route path="/faqs" element={<Guide />} />
        
        {/* Public routes that need authentication check (redirect to  if authenticated) */}
        <Route element={<AuthRoute />}>
          <Route path="/login" element={<Login />} />
          <Route path="/verify" element={<Verify />} />
        </Route>
        
        {/* Admin login - public route */}
        <Route path="/admin/login" element={<AdminLogin />} />
        
        {/* Complete registration route - accessible with partial authentication */}
        <Route path="/complete-registration" element={<CompleteRegistration />} />
        
        {/* Protected routes (redirect to login if not authenticated) */}
        <Route element={<ProtectedRoute />}>
          <Route path="/home" element={<Home />} />
    
          <Route path="/transactions" element={<Transactions />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/payment-callback" element={<PaymentCallback />} />
          <Route path="/account-status" element={<AccountStatus />} />
          <Route path="/help" element={<Help />} />
          
          {/* New Routes */}
          <Route path="/new-test-request" element={<NewTestRequest />} />
          <Route path="/edit-request/:id" element={<NewTestRequest />} />
          <Route path="/my-tests" element={<MyTests />} />
          <Route path="/receive-image-report" element={<ReceiveImageReport />} />
          <Route path="/view-online-results" element={<ViewOnlineResults />} />
          <Route path="/submit-ticket" element={<SubmitTicket />} />
          <Route path="/tickets" element={<Tickets />} />
          <Route path="/tickets/:id" element={<TicketDetails />} />
          <Route path="/bank-payment" element={<BankPayment />} />
          <Route path="/view-test-results" element={<ViewTestResults />} />
          <Route path="/system-messages" element={<SystemMessages />} />
          <Route path="/submit-request" element={<SubmitRequest />} />
          <Route path="/test-request-guide" element={<TestRequestGuide />} />
          <Route path="/test-results-guide" element={<TestResultsGuide />} />
          <Route path="/test-requests/:id" element={<ViewRequestStatus />} />
          <Route path="/messages" element={<UserMessages />} />
          
        </Route>
        
        {/* Admin Protected Routes - All wrapped in DashboardLayout */}
        <Route element={<AdminProtectedRoute />}>
          <Route path="/admin" element={<DashboardLayout><Dashboard /></DashboardLayout>} />
          <Route path="/admin/dashboard" element={<DashboardLayout><Dashboard /></DashboardLayout>} />
          <Route path="/admin/access-control" element={<DashboardLayout><AccessControl /></DashboardLayout>} />
          <Route path="/admin/operators" element={<DashboardLayout><Operators /></DashboardLayout>} />
          <Route path="/admin/users" element={<DashboardLayout><Users /></DashboardLayout>} />
          <Route path="/admin/ongoing-requests" element={<DashboardLayout><OngoingRequests /></DashboardLayout>} />
          <Route path="/admin/completed-requests" element={<DashboardLayout><CompletedRequests /></DashboardLayout>} />
          <Route path="/admin/others-requests" element={<DashboardLayout><OtherRequests /></DashboardLayout>} />
          <Route path="/admin/calendar" element={<DashboardLayout><Calendar /></DashboardLayout>} />
          <Route path="/admin/general-settings" element={<DashboardLayout><GeneralSettings /></DashboardLayout>} />
          <Route path="/admin/region-settings" element={<DashboardLayout><RegionSettings /></DashboardLayout>} />
          <Route path="/admin/links" element={<DashboardLayout><Links /></DashboardLayout>} />
          <Route path="/admin/guides" element={<DashboardLayout><AdminGuide /></DashboardLayout>} />
          <Route path="/admin/faqs" element={<DashboardLayout><AdminFAQ /></DashboardLayout>} />
          <Route path="/admin/reports" element={<DashboardLayout><Reports /></DashboardLayout>} />

          <Route path="/admin/test-packages" element={<DashboardLayout><TestPackages /></DashboardLayout>} />
          <Route path="/admin/insurance" element={<DashboardLayout><Insurance /></DashboardLayout>} />
          <Route path="/admin/messages" element={<DashboardLayout><Messages /></DashboardLayout>} />
          <Route path="/admin/commands" element={<DashboardLayout><Commands /></DashboardLayout>} />
          <Route path="/admin/image-report-requests" element={<DashboardLayout><ImageReportRequests /></DashboardLayout>} />
          <Route path="/admin/bank-payment-requests" element={<DashboardLayout><BankPaymentRequests /></DashboardLayout>} />
          <Route path="/admin/tickets" element={<DashboardLayout><AdminTickets /></DashboardLayout>} />
          <Route path="/admin/tickets/:id" element={<DashboardLayout><AdminTicketDetails /></DashboardLayout>} />
        </Route>
        
        {/* Fallback for unknown routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
